import { RefObject } from "react"
import { action, computed, makeObservable, observable } from "mobx"
import { ProgressProps, UploadFile } from "antd"
import { UploadChangeParam } from "antd/es/upload"
import { UploadRef } from "antd/es/upload/Upload"

import { loading } from "@src/shared/classes/utils/loading"
import { IImageUploadState } from "@src/shared/componemts/ImageUploadUI/entity/ImageUpload"

export interface IImageUploadProgress {
  percent: number
  status: ProgressProps["status"]
}

export interface IUploadedState {
  progress: IImageUploadProgress
  ref: RefObject<UploadRef> | null
  source: UploadFile | null
}

const defaultProgress = (): IImageUploadProgress => ({
  percent: 0,
  status: "normal",
})

export interface IImageUploadProgress {
  percent: number
  status: ProgressProps["status"]
}

export const defaultUploadedState: IUploadedState = {
  progress: defaultProgress(),
  ref: null,
  source: null,
}

type UpdateProgressOptions = {
  delay?: number
  end: number
  status?: IImageUploadProgress["status"]
}

export class Uploaded extends loading(["upload"]) {
  _state: IUploadedState = { ...defaultUploadedState }

  constructor() {
    super()

    makeObservable(this, {
      _state: observable.shallow,
      nativeInput: computed,
      onChange: action.bound,
      remove: action.bound,
      replace: action.bound,
      setRef: action.bound,
      setState: action.bound,
      updateProcess: action.bound,
    })
  }

  async _upload(file: Blob): Promise<void> {
    // Must override this
  }

  async onChange({ file, fileList }: UploadChangeParam<UploadFile<any>>) {
    this.setState({
      source: fileList[0] as UploadFile,
    })

    if (this.hasFile) {
      return this.upload()
    }
  }

  remove() {
    this.setState({ source: null })
  }

  replace() {
    this.remove()

    if (this.nativeInput) {
      this.nativeInput.click()
    }
  }

  setRef(ref: RefObject<UploadRef>) {
    this.setState({ ref: ref })
  }

  setState(state: Partial<IImageUploadState>) {
    this._state = {
      ...this._state,
      ...state,
    }
  }

  async updateProcess({ delay = 10, end, status }: UpdateProgressOptions) {
    let diff = end - this._state.progress.percent

    return new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (diff <= 0) {
          clearInterval(intervalId)

          resolve(true)
        } else {
          diff--

          this.setState({
            progress: {
              ...this._state.progress,
              percent: this._state.progress.percent + 1,
              ...(status ? { status: status } : {}),
            },
          })
        }
      }, delay)
    })
  }

  async upload() {
    this.setState({ progress: defaultProgress() })

    await this.updateProcess({ end: 10, status: "active" })

    try {
      if (this._state.source) {
        await Promise.all([
          this.updateProcess({ delay: 20, end: 80 }),
          this._upload(this._state.source.originFileObj as Blob),
        ])

        await this.updateProcess({ delay: 30, end: 100, status: "success" })
      } else {
        await this.updateProcess({ delay: 30, end: 100, status: "success" })
      }
    } catch (e) {
      await this.updateProcess({ end: 80, status: "exception" })
    }

    return Promise.resolve()
  }

  get hasFile() {
    return Boolean(this._state.source)
  }

  get nativeInput() {
    const ref = this._state.ref

    if (ref) {
      return (
        ((ref.current?.upload as Record<string, any>)?.uploader
          .fileInput as HTMLInputElement) ?? null
      )
    }

    return ref
  }

  get progress(): IImageUploadProgress {
    return this._state.progress
  }
}
